import { Editor, Node } from '@tiptap/core';
import { NodeSelection, Plugin, TextSelection } from '@tiptap/pm/state';
import { closeHistory } from '@tiptap/pm/history';
import type { Node as PMNode } from '@tiptap/pm/model';
import { FormFieldDto } from '../../general/dtos';
import { defaultFieldLayout, fieldLayoutAttributes, normalizeFieldLayout, readFieldLayout } from '../../general/field-container-layout';

declare module '@tiptap/core' {
    interface Storage { fieldContainer: { onEdit: ((position: number) => void) | null } }
}
export function insideFieldContainer(editor: Editor): boolean {
    if (!editor.state?.selection) return false;
    if (editor.state.selection instanceof NodeSelection && editor.state.selection.node.type.name === 'fieldContainer') return true;
    const { $from, $to } = editor.state.selection;
    return [$from, $to].some(position => {
        for (let depth = position.depth; depth > 0; depth--) if (position.node(depth).type.name === 'fieldContainer') return true;
        return false;
    });
}
export function insertFormField(editor: Editor, field: FormFieldDto): boolean {
    if (field.type === 'quiz-list') return false;
    return editor.commands.command(({ tr, dispatch }) => {
        if (dispatch) tr.replaceSelectionWith(editor.schema.nodes.formField.create({ key: field.key }), false);
        return true;
    });
}

/** Read a field-only selection, including ordinary spacing and paragraph boundaries. */
export function selectedContainerFields(editor: Editor): PMNode[] | null {
    if (editor.state.selection.empty) return [];
    if (insideFieldContainer(editor)) return null;
    const fields: PMNode[] = [];
    let valid = true;
    const getField = editor.extensionManager.extensions.find(extension => extension.name === 'formField')?.options.onGetField;
    const visit = (node: PMNode) => {
        if (node.type.name === 'formField') {
            if (!node.attrs.key || node.attrs['field-key'] || node.attrs['expected-key'] || getField?.(node.attrs.key)?.type === 'quiz-list') valid = false;
            else fields.push(node.mark([]));
        } else if (node.type.name === 'fieldContainer') valid = false;
        else if (node.isTextblock) node.forEach(visit);
        else if (node.type.name === 'hardBreak') return;
        else if (!node.isText || node.text?.trim()) valid = false;
    };
    editor.state.selection.content().content.forEach(visit);
    return valid && fields.length ? fields : null;
}

function deleteContainerField(editor: Editor, backwards: boolean): boolean {
    const { selection } = editor.state;
    if (selection.$from.parent.type.name !== 'fieldContainer' || selection.$to.parent !== selection.$from.parent) return false;
    if (!selection.empty) {
        return editor.commands.command(({ tr }) => { closeHistory(tr); tr.deleteSelection(); return true; });
    }
    const neighbour = backwards ? selection.$from.nodeBefore : selection.$from.nodeAfter;
    if (neighbour?.type.name !== 'formField') return true;
    const from = backwards ? selection.from - neighbour.nodeSize : selection.from;
    const to = backwards ? selection.from : selection.from + neighbour.nodeSize;
    return editor.commands.command(({ tr }) => {
        closeHistory(tr);
        tr.delete(from, to).setSelection(TextSelection.create(tr.doc, from));
        return true;
    });
}

function moveContainerCursor(editor: Editor, backwards: boolean): boolean {
    const { selection } = editor.state;
    if (selection.$from.parent.type.name !== 'fieldContainer') return false;
    if (selection instanceof NodeSelection) return editor.commands.setTextSelection(backwards ? selection.from : selection.to);
    if (!selection.empty) return false;
    const neighbour = backwards ? selection.$from.nodeBefore : selection.$from.nodeAfter;
    if (neighbour?.type.name !== 'formField') return false;
    return editor.commands.setTextSelection(selection.from + (backwards ? -neighbour.nodeSize : neighbour.nodeSize));
}

export const FieldContainerNode = Node.create({
    name: 'fieldContainer', group: 'block', content: 'formField*', marks: '', isolating: true,
    addStorage() { return { onEdit: null }; },
    addAttributes() {
        return Object.fromEntries(Object.entries(defaultFieldLayout).map(([key, value]) => [key, {
            default: value, parseHTML: (element: Element) => readFieldLayout(element)[key as keyof typeof defaultFieldLayout],
            renderHTML: () => ({})
        }]));
    },
    parseHTML() { return [{ tag: 'field-container' }]; },
    renderHTML({ node }) { return ['field-container', fieldLayoutAttributes(normalizeFieldLayout(node.attrs)), 0]; },
    addKeyboardShortcuts() {
        return {
            Enter: () => insideFieldContainer(this.editor),
            Backspace: () => deleteContainerField(this.editor, true),
            Delete: () => deleteContainerField(this.editor, false),
            ArrowLeft: () => moveContainerCursor(this.editor, true),
            ArrowRight: () => moveContainerCursor(this.editor, false)
        };
    },
    addNodeView() {
        return props => {
            const doc = (props.editor.options.element as HTMLElement | null)?.ownerDocument ?? document;
            const dom = doc.createElement('div');
            dom.className = 'field-container-editor';
            dom.style.cssText = 'box-sizing:border-box;max-width:100%;border:1px dashed var(--primary-color,currentColor);border-radius:4px;padding:0.5rem;margin:0.5rem 0;';
            dom.style.width = normalizeFieldLayout(props.node.attrs).width;
            const header = doc.createElement('div');
            header.contentEditable = 'false';
            header.style.cssText = 'display:flex;flex-wrap:wrap;gap:8px;margin-bottom:6px';
            const edit = doc.createElement('button'); edit.type = 'button'; edit.textContent = 'Properties…';
            edit.onclick = () => { const pos = props.getPos(); if (typeof pos === 'number') this.storage.onEdit?.(pos); };
            const remove = doc.createElement('button'); remove.type = 'button'; remove.textContent = 'Delete container';
            header.append(edit, remove);
            const contentDOM = doc.createElement('div');
            contentDOM.style.cssText = 'min-height:2.5rem;display:block;white-space:normal;cursor:text;';
            dom.append(header, contentDOM);
            let currentNode = props.node;
            remove.onclick = () => {
                const pos = props.getPos();
                if (typeof pos === 'number') props.editor.chain().command(({ tr }) => {
                    closeHistory(tr); tr.delete(pos, pos + currentNode.nodeSize); return true;
                }).run();
            };
            return { dom, contentDOM, update(node) {
                if (node.type !== currentNode.type) return false;
                currentNode = node;
                dom.style.width = normalizeFieldLayout(node.attrs).width;
                return true;
            } };
        };
    },
    addProseMirrorPlugins() {
        const editor = this.editor;
        const outside = (doc: PMNode): string => {
            const strip = (node: PMNode): unknown => {
                if (node.type.name === 'fieldContainer' || (node.type.name === 'paragraph' && !node.content.size)) return null;
                if (node.isLeaf) return node.toJSON();
                const children: unknown[] = [];
                node.forEach(child => { const stripped = strip(child); if (stripped) children.push(stripped); });
                return { ...node.toJSON(), content: children };
            };
            return JSON.stringify(strip(doc));
        };
        const countContainers = (doc: PMNode): number => {
            let count = 0;
            doc.descendants(node => { if (node.type.name === 'fieldContainer') count++; });
            return count;
        };
        return [new Plugin({
            props: {
                handleTextInput: () => insideFieldContainer(editor),
                handlePaste: () => insideFieldContainer(editor),
                handleDrop: (_view, event) => insideFieldContainer(editor) || !!(event.target as Element | null)?.closest?.('.field-container-editor'),
                handleDOMEvents: { beforeinput: (_view, event) => {
                    if (insideFieldContainer(editor) && (event as InputEvent).inputType?.startsWith('insert')) { event.preventDefault(); return true; }
                    return false;
                } }
            },
            filterTransaction(tr, state) {
                if (!tr.docChanged) return true;
                let valid = true;
                const getField = editor.extensionManager.extensions.find(extension => extension.name === 'formField')?.options.onGetField;
                tr.doc.descendants(node => {
                    if (node.type.name !== 'fieldContainer') return;
                    node.forEach(child => {
                        if (child.type.name !== 'formField' || child.marks.length || child.attrs['expected-key'] || child.attrs['field-key']
                            || getField?.(child.attrs.key)?.type === 'quiz-list') valid = false;
                    });
                });
                if (!valid) return false;
                // Prevent schema fitting from moving unsupported content just outside the container.
                if (insideFieldContainer(editor) && !tr.getMeta('preventUpdate') && !Object.keys((tr as any).meta).some(key => key.startsWith('history$'))
                    && (outside(state.doc) !== outside(tr.doc) || countContainers(tr.doc) > countContainers(state.doc))) return false;
                return true;
            }
        })];
    }
});
